import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GroupItem, GroupRequest } from "../../../types/groupItem";
import { ActionModule } from "../../../types/actionModule/actionModule";
import { GeneralButton } from "../../../common/ui/GeneralButton";
import ActionButton from "../../../components/common/table/ActionButton";
import SelectEmployeesDialog from "../../../components/employees/SelectEmployeesDialog";
import { EmployeeGeneral } from "../../../types/employeeType";
import { groupsApi } from "../../../api/groupsApi";
import SelectActionModulesDialog from "../../../components/actionModules/SelectActionModulesDialog";

interface FormData {
  name: string;
  actionModules: ActionModule[];
  employeeIds: string[];
}

interface Props {
  onClose: () => void;
  onSave: (group: GroupRequest, id: number | undefined) => void;

  group?: GroupItem;
}

export default function EditGroupDialog(props: Props) {
  const { t } = useTranslation();

  const [error, setError] = useState("");
  const [openModulesDialog, setOpenModulesDialog] = useState(false);
  const [openEmployeesDialog, setOpenEmployeesDialog] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    actionModules: [],
    employeeIds:[]
  });

  const loadMembers = async (groupId: number) => {
    const response = await groupsApi.getGroupMembers(groupId);
    if (response.success() && response.result) {
      setFormData((prev) => ({
        ...prev,
        employeeIds: response.result!.map(e => e.id)
      }));
    }
  };

  useEffect(() => {
    setFormData({
      name: props.group?.name ?? "",
      actionModules: props.group?.actionModules ?? [],
      employeeIds: []
    });

    if (props.group) {
      loadMembers(props.group.id);
    }

  }, [props.group]);


  const validateInput = (): boolean => {
    const trimmed = formData.name.trim();
    if (trimmed.length === 0) {
      setError(t("dialogModals.fieldNotFilled"));

      return false;
    } else {
      setError("");

      return true;
    }
  }

  const handleSave = async () => {
    const valid = validateInput();
    if (!valid) {
      return;
    }

    const groupData: GroupRequest = {
      name: formData.name,
      actionModuleIds: formData.actionModules.map((m) => m.id),
      memberIds: formData.employeeIds
    };

    props.onSave(groupData, props.group?.id);
    props.onClose();
  };

  return (
    <>
      <Dialog open={true} onClose={props.onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("label.createNewGroup")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t("groupsPage.placeholders.incentiveGroupName")}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            onBlur={validateInput}
            error={!!error}
            helperText={error}
            sx={{ mt: 1 }}
          />

          <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>
            {t("groupsPage.groups.selectActionModules")}
          </Typography>
          <SelectAcitonModulesPart
            formData={formData}
            onEditActionModulesClick={() => setOpenModulesDialog(true)}
          />

          <Typography variant="subtitle1" sx={{ mt: 4, mb: 1 }}>
            {t("groupsPage.groups.selectGroupMembers")}
          </Typography>
          <SelectEmployeesPart
            formData={formData}
            onEditEmployeesClick={() => setOpenEmployeesDialog(true)}
          />
        </DialogContent>

        <DialogActions>
          <GeneralButton
            appVariant="secondary"
            onClick={props.onClose}
          >
            {t("buttons.cancel")}
          </GeneralButton>

          <GeneralButton
            appVariant="primary"
            onClick={handleSave}
            disabled={!!error}
          >
            {t("buttons.save")}
          </GeneralButton>
        </DialogActions>
      </Dialog>

      {
        openModulesDialog && <SelectActionModulesDialog
          onClose={() => setOpenModulesDialog(false)}
          selectedActions={formData.actionModules}
          handleOnSave={(value) => {
            setFormData({ ...formData, actionModules: value });
            setOpenModulesDialog(false);
          }}
        />
      }
      {
        openEmployeesDialog && <SelectEmployeesDialog
          onClose={() => setOpenEmployeesDialog(false)}
          selectedEmployees={formData.employeeIds}
          handleOnSave={(selectedEmployeeIds) => {
            setFormData({ ...formData, employeeIds: selectedEmployeeIds });
            setOpenEmployeesDialog(false);
          }}
        />
      }
    </>
  );
};

interface SelectActionModulesPartProps {
  formData: FormData;
  onEditActionModulesClick: () => void;
}

function SelectAcitonModulesPart(props: SelectActionModulesPartProps) {
  const { t } = useTranslation();

  return <Stack sx={{ alignItems: "start" }}>
    <GeneralButton
      appVariant="secondary"
      onClick={props.onEditActionModulesClick}
    >
      {t("groupsPage.groups.selectActionModulesButton")}
    </GeneralButton>
    <Stack direction="row" flexWrap="wrap" sx={{ mt: 2 }} gap={1} rowGap={1}>
      {
        props.formData.actionModules.map((a) => <ActionButton
          key={a.id}
          actionName={a.name}
          iconName={a.iconName}
          colorHex={a.colorHex}
          iconUrl={a.iconUrl}
          isEditMode={false}
          handleActionButtonDelete={undefined} />
        )
      }
    </Stack>
  </Stack>
}


interface SelectEmployeesPartProps {
  formData: FormData;
  onEditEmployeesClick: () => void;
}

function SelectEmployeesPart(props: SelectEmployeesPartProps) {
  const { t } = useTranslation();

  return <Stack sx={{ alignItems: "start" }}>
    {
      props.formData.employeeIds ? <>
        <GeneralButton
          appVariant="secondary"
          onClick={props.onEditEmployeesClick}
        >
          {t("groupsPage.groups.selectGroupMembersButton")}
        </GeneralButton>
        <Typography sx={{ mt: 2 }}>
          {
            props.formData.employeeIds.length === 0
              ? t("groupsPage.groups.selectGroupMembersNoMember")
              : t("groupsPage.groups.selectGroupMembersNumberMember", { count: props.formData.employeeIds.length })
          }
        </Typography>

      </>
        : <CircularProgress />
    }
  </Stack>
}